import { motion } from 'framer-motion';
import { ArrowDown, Users, ShieldCheck, Crosshair } from 'lucide-react';

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_precision-connect/artifacts/9tvt50id_video.mp4";

const pillars = [
  { icon: Users, text: 'Top 3 perfiles.' },
  { icon: ShieldCheck, text: 'NDA-first.' },
  { icon: Crosshair, text: 'Criterio antes que volumen.' },
];

const lineReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.3 + i * 0.15, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video">
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <motion.div
          className="absolute inset-0 bg-[#1B2A4A]/85"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
        />
        <div className="grain absolute inset-0" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8 lg:col-span-7">
            <motion.div
              custom={0}
              initial="hidden"
              animate="visible"
              variants={lineReveal}
              className="mb-5"
            >
              <span className="text-[#C58C40] text-xs md:text-sm font-medium tracking-[0.2em] uppercase whitespace-normal md:whitespace-nowrap">
                — Antes solo para los grandes. Ahora para empresas como la suya
              </span>
            </motion.div>

            <div className="overflow-hidden mb-8">
              <motion.h1
                data-testid="hero-headline"
                custom={1}
                initial="hidden"
                animate="visible"
                variants={lineReveal}
                className="text-3xl sm:text-4xl lg:text-[2.85rem] font-normal tracking-[0.02em] leading-[1.2] text-white"
              >
                El ecosistema donde las mejores decisiones
                <br />
                empiezan por las personas
              </motion.h1>
            </div>

            <motion.p
              data-testid="hero-subheadline"
              custom={2}
              initial="hidden"
              animate="visible"
              variants={lineReveal}
              className="text-base md:text-lg font-normal leading-relaxed text-[#8A9BB1] max-w-lg mb-14"
            >
              Identificamos y conectamos a las personas adecuadas para cada desafío
              empresarial mediante un proceso confidencial, estructurado y basado en criterio.
            </motion.p>

            <motion.div
              custom={3}
              initial="hidden"
              animate="visible"
              variants={lineReveal}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollTo('#final-cta')}
                data-testid="hero-primary-cta"
                className="bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-8 py-4 text-sm tracking-[0.15em] uppercase"
              >
                Iniciar Mandato
              </button>
              <button
                onClick={() => scrollTo('#proceso')}
                data-testid="hero-secondary-cta"
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 transition-colors duration-300 rounded-sm px-8 py-4 text-sm tracking-[0.15em] uppercase"
              >
                Descubrir Conadvy
              </button>
            </motion.div>
          </div>
        </div>

        <motion.div
          custom={4}
          initial="hidden"
          animate="visible"
          variants={lineReveal}
          className="mt-24 md:mt-32 border-t border-white/10 pt-10"
        >
          <p className="text-white/70 text-lg md:text-xl font-normal leading-relaxed max-w-2xl mb-8">
            Lo que diferencia a las empresas que escalan no es el tamaño. Es con quién deciden.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-12">
            {pillars.map((p, i) => (
              <motion.div
                key={i}
                custom={5 + i}
                initial="hidden"
                animate="visible"
                variants={lineReveal}
                className="flex items-center gap-3"
              >
                <p.icon size={15} className="text-[#C58C40]" strokeWidth={1.5} />
                <span className="text-[#8A9BB1] text-sm tracking-wide">{p.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <ArrowDown size={16} className="text-white/20 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}
